<template>
  <div class="wrraper sms_logs_page">
    <!-- Start Breadcrumb -->
    <Breadcrumb :items="items" />
    <!-- End Breadcrumb -->

    <!-- Start 403 State -->
    <div v-if="forbidden" class="forbidden_state text-center">
      <i class="fal fa-lock mb-3"></i>
      <p>{{ forbiddenMessage || $t("smsLogs.forbidden") }}</p>
    </div>
    <!-- End 403 State -->

    <template v-else>
      <div class="card-header mb-4">
        <h4 class="card-title">
          <i class="fas fa-sms ml-2"></i>
          {{ $t("smsLogs.title") }}
          <span class="total">({{ meta.total }})</span>
        </h4>
      </div>

      <!-- Start Filters -->
      <div class="d-flex align-items-center filters-row">
        <the-filter
          class="flex-grow-1"
          :searchLabel="$t('smsLogs.filters.mobile')"
          :customName2="$t('smsLogs.filters.status')"
          :inputs="inputs"
          :types="statusItems"
        />
        <button
          type="button"
          class="reset-btn flex-shrink-0"
          :title="$t('smsLogs.filters.reset')"
          @click="resetFilters"
        >
          <i class="fal fa-undo"></i>
        </button>
      </div>
      <!-- End Filters -->

      <!-- Start Table (read only, no create / edit / delete) -->
      <main class="magic-table-wrapper">
        <v-data-table
          class="thumb strip"
          :headers="headers"
          :items="rows"
          :loading="loading"
          :loading-text="$t('table.loadingData')"
          item-key="id"
          :items-per-page="-1"
          disable-sort
          hide-default-footer
        >
          <template v-slot:[`item.provider`]="{ item }">
            {{ providerLabel(item.provider) }}
          </template>

          <template v-slot:[`item.mobile`]="{ item }">
            <span dir="ltr">{{ (item.phone_code || "") + item.mobile }}</span>
          </template>

          <template v-slot:[`item.message`]="{ item }">
            <div class="sms_message">{{ item.message }}</div>
          </template>

          <template v-slot:[`item.status`]="{ item }">
            <span :class="['status_label', item.status]">
              {{ statusLabel(item.status) }}
            </span>
          </template>

          <template v-slot:[`item.http_status`]="{ item }">
            {{ dash(item.http_status) }}
          </template>

          <template v-slot:[`item.provider_code`]="{ item }">
            {{ dash(item.provider_code) }}
          </template>

          <template v-slot:[`item.response_excerpt`]="{ item }">
            <div class="sms_message">{{ dash(item.response_excerpt) }}</div>
          </template>

          <template v-slot:[`item.error_message`]="{ item }">
            <div class="sms_message text-danger">
              {{ dash(item.error_message) }}
            </div>
          </template>

          <template v-slot:[`item.created_at`]="{ item }">
            <span dir="ltr">{{ item.created_at }}</span>
          </template>

          <template v-slot:no-data>
            {{ $t("table.noData") }}
          </template>
        </v-data-table>
      </main>
      <!-- End Table -->

      <!-- Start Pagination -->
      <div
        class="pagination_container text-center my-4 d-flex justify-content-end"
      >
        <div class="select-pagination d-flex">
          <span class="first">{{ $t("show") }}</span>
          <v-select
            :items="perPageOptions"
            v-model="perPage"
            @change="onPerPageChange"
          ></v-select>
          <span>{{ $t("entries") }}</span>
        </div>

        <v-pagination
          v-if="meta.last_page > 1"
          v-model.number="page"
          :length="meta.last_page"
          :total-visible="5"
          :disabled="loading"
          @input="fetchData"
        ></v-pagination>
      </div>
      <!-- End Pagination -->
    </template>
  </div>
</template>

<script>
import TheFilter from "@/components/Filters/TheFilter.vue";

export default {
  name: "SmsLogs",

  components: { TheFilter },

  data() {
    return {
      // ========== Breadcrumbs
      items: [
        {
          text: this.$t("breadcrumb.mainPage"),
          disabled: false,
          href: "/",
        },
        {
          text: this.$t("breadcrumb.sms_logs.title"),
          disabled: true,
          href: "",
        },
      ],

      providers: ["vodafone", "msegat"],
      statuses: ["sent", "failed"],
      perPageOptions: [15, 30, 50, 100],
      perPage: 15,

      // Same filter component as the other pages, driven by the route query
      inputs: [
        { keyName: "keyword" },
        { keyName: "type" },
        { keyName: "start_date" },
        { keyName: "end_date" },
      ],

      rows: [],
      page: 1,
      meta: { current_page: 1, last_page: 1, per_page: 15, total: 0 },

      loading: false,
      forbidden: false,
      forbiddenMessage: "",
      // Ignore a slow response when a newer request was already sent
      requestId: 0,
    };
  },

  computed: {
    statusItems() {
      return this.statuses.map((id) => ({
        id,
        name: this.$t(`smsLogs.statuses.${id}`),
      }));
    },

    headers() {
      const col = (key) => this.$t(`smsLogs.columns.${key}`);
      return [
        { text: col("id"), value: "id", align: "center" },
        { text: col("provider"), value: "provider", align: "center" },
        { text: col("mobile"), value: "mobile", align: "center" },
        { text: col("message"), value: "message", align: "center" },
        { text: col("status"), value: "status", align: "center" },
        { text: col("httpStatus"), value: "http_status", align: "center" },
        { text: col("providerCode"), value: "provider_code", align: "center" },
        {
          text: col("response"),
          value: "response_excerpt",
          align: "center",
        },
        { text: col("error"), value: "error_message", align: "center" },
        { text: col("date"), value: "created_at", align: "center" },
      ];
    },
  },

  watch: {
    // Every filter / page change made in the-filter lands in the route query
    "$route.query"() {
      this.page = +this.$route.query.page || 1;
      this.getLogs();
    },
  },

  methods: {
    providerLabel(value) {
      return this.providers.includes(value)
        ? this.$t(`smsLogs.providers.${value}`)
        : value;
    },

    statusLabel(value) {
      return this.statuses.includes(value)
        ? this.$t(`smsLogs.statuses.${value}`)
        : value;
    },

    dash(value) {
      return value === null || value === undefined || value === ""
        ? "-"
        : value;
    },

    // Only send the filters the user actually filled
    buildParams() {
      const q = this.$route.query;
      const params = { per_page: this.perPage, page: this.page };
      if (q.keyword) params.mobile = q.keyword;
      if (q.type) params.status = q.type;
      if (q.start_date) params.from = q.start_date;
      if (q.end_date) params.to = q.end_date;
      return params;
    },

    resetFilters() {
      this.$router.push({ query: { page: 1 } }).catch(() => {});
    },

    fetchData(page) {
      this.$router
        .push({ query: { ...this.$route.query, page } })
        .catch(() => {});
    },

    onPerPageChange() {
      this.page = 1;
      if (+this.$route.query.page > 1) {
        // The route watcher reloads the list
        this.fetchData(1);
      } else {
        this.getLogs();
      }
    },

    getLogs() {
      const currentRequest = ++this.requestId;
      this.loading = true;

      this.$axios({
        method: "GET",
        url: "sms_logs",
        params: this.buildParams(),
      })
        .then((res) => {
          if (currentRequest !== this.requestId) return;
          const body = res.data || {};
          this.rows = Array.isArray(body.data) ? body.data : [];
          this.meta = { ...this.meta, ...(body.meta || {}) };
          this.loading = false;
        })
        .catch((err) => {
          if (currentRequest !== this.requestId) return;
          // The spinner never stays after 403 / 422
          this.loading = false;
          const status = err.response?.status;
          const message = err.response?.data?.message;

          if (status === 403) {
            this.forbidden = true;
            this.forbiddenMessage = message || "";
            return;
          }
          // 401 is handled by the global axios interceptor (admin logout)
          if (status === 401) return;

          // 422 shows the first message, the screen and filters stay
          this.$iziToast.error({
            timeout: 3000,
            message: this.firstError(err) || this.$t("globalErr"),
            position: "bottomRight",
          });
        });
    },

    firstError(err) {
      const data = err.response?.data;
      if (data?.errors) {
        const first = Object.values(data.errors)[0];
        if (Array.isArray(first) && first.length) return first[0];
      }
      return data?.message || "";
    },
  },

  created() {
    this.page = +this.$route.query.page || 1;
    this.getLogs();
  },
};
</script>

<style lang="scss" scoped>
.sms_logs_page {
  .card-header {
    background: transparent;
    border: none;
    padding: 0;

    .card-title {
      font-weight: bold;
      margin-bottom: 4px;

      .total {
        font-size: 14px;
        color: #8a8a8a;
      }
    }
  }

  .filters-row {
    column-gap: 10px;

    .reset-btn {
      width: 46px;
      height: 46px;
      flex: 0 0 46px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      border: 1px solid var(--mainColor-2);
      border-radius: 50%;
      background-color: transparent;
      color: var(--mainColor-2);
      cursor: pointer;
      transition: all 0.25s ease;

      &:hover,
      &:focus {
        background-color: var(--mainColor-2);
        color: #fff;
        outline: none;
      }
    }
  }

  .forbidden_state {
    padding: 60px 0;

    i {
      font-size: 5rem;
      color: #8a8a8a;
    }
  }

  .sms_message {
    max-width: 280px;
    margin: 0 auto;
    white-space: pre-wrap;
    word-break: break-word;
  }

  .status_label {
    display: inline-block;
    padding: 2px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: bold;

    &.sent {
      color: #1b5e20;
      background: rgba(76, 175, 80, 0.15);
    }

    &.failed {
      color: #b71c1c;
      background: rgba(244, 67, 54, 0.15);
    }
  }
}
</style>
